import { createContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import Cookies from "js-cookie";

const DataContext = createContext();

const DataProvider = ({ children }) => {
  const apiDomain = process.env.REACT_APP_API_DOMAIN;
  const supportDomain = process.env.REACT_APP_SUPPORT_DOMAIN;
  const notificationDomain = process.env.REACT_APP_NOTIFICATION_DOMAIN;

  // State variables
  const [eventData, setEventData] = useState([]);
  const [singleEvent, setSingleEvent] = useState(null);
  const [participantsMonthly, setParticipantsMonthly] = useState([]);
  const [participantsTotal, setParticipantsTotal] = useState([]);
  const [participant, setParticipant] = useState([]);
  const [noneParticipated, setNoneParticipated] = useState([]);
  const [location, setLocation] = useState([]);
  const [venues, setVenues] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [filteredSponsor, setFilteredSposnor] = useState([]);
  const [support, setSupport] = useState([]);
  const [collabTickets, setCollabTickets] = useState([]);
  const [winners, setWinners] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [usersInfo, setUsersInfo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allEventNames, setAllEventNames] = useState([]);
  const [allEventsSummary, setAllEventsSummary] = useState([]);

 
const getEvtId = (e) => String(e?.eventID ?? e?.eventId ?? e?.id ?? "");
const normalizeEvent = (e) => {
  const id = getEvtId(e);
  return id ? { ...e, eventID: id } : e;
};

// keep as-is insert when not present (no mutation)
const prependEvent = useCallback((evt) => {
  if (!evt) return;
  const incoming = normalizeEvent(evt);
  const id = getEvtId(incoming);
  setEventData((prev) => {
    if (!id) return prev;
    const exists = prev.some((e) => getEvtId(e) === id);
    return exists ? prev : [incoming, ...prev];
  });
}, []);

// NEW: works for both create (insert) and update (replace in-place)
const upsertEvent = useCallback(
  (evt, { keepPositionIfExists = true, putOnTopIfNew = true } = {}) => {
    if (!evt) return;
    const incoming = normalizeEvent(evt);
    const id = getEvtId(incoming);
    if (!id) return;

    setEventData((prev) => {
      const idx = prev.findIndex((e) => getEvtId(e) === id);
      if (idx === -1) {
        return putOnTopIfNew ? [incoming, ...prev] : [...prev, incoming];
      }
      const copy = prev.slice();
      copy[idx] = { ...copy[idx], ...incoming }; // replace in-place
      return keepPositionIfExists
        ? copy
        : [copy[idx], ...copy.filter((_, i) => i !== idx)];
    });
  },
  []
);

// MERGE server data (don’t wipe optimistic items)
  const fetchAllEvents = useCallback(async (filter = 'PastweekEvents', startDate = undefined, endDate = undefined, replace = false) => {
  try {
    const params = filter && filter !== 'all' ? { filter } : {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    
    // Request timestamp fields
    params.fields = 'eventID,eventName,startDate,endDate,description,eventType,createdAt,updatedAt,additional_data,templateData,termsConditions,instructions,votingDuration,sponsorData';
    
    const res = await axios.get(`${apiDomain}/api/v2/getfilteredevent`, {
      params: { ...params, pageSize: 100, page: 1, t: Date.now() },
    });
    const events = res?.data?.events || [];
    setEventData((prev) => {
      if (replace) {
        return events.map(normalizeEvent);
      }
      const byId = new Map();
      // keep optimistic/current first
      for (const e of prev) {
        const id = getEvtId(e);
        if (id) byId.set(id, e);
      }
      // overlay server truth
      for (const e of events) {
        const id = getEvtId(e);
        if (id) byId.set(id, normalizeEvent(e));
      }

      // preserve server order first
      const serverOrder = events.map(getEvtId).filter(Boolean);
      const merged = [];
      const pushed = new Set();
      for (const id of serverOrder) {
        if (byId.has(id)) {
          merged.push(byId.get(id));
          pushed.add(id);
        }
      }
      for (const [id, e] of byId.entries()) {
        if (!pushed.has(id)) merged.push(e);
      }
      return merged;
    });
  } catch {
    // keep prev on error
    setEventData((prev) => prev);
  }
}, [apiDomain]);


  // Fetch all event names for dropdowns
  const fetchAllEventNames = useCallback(async () => {
    try {
      const res = await axios.get(`${apiDomain}/api/v2/getallevents`, {
        params: { t: Date.now() },
      });
      const events = res?.data || [];
      const names = events.map((e) => e.event_name).filter(Boolean);
      setAllEventNames(names);
      setAllEventsSummary(events); // Store full event summary data
    } catch (err) {
      setAllEventNames([]);
      setAllEventsSummary([]);
    }
  }, [apiDomain]);


  // Fetch single event by ID
  const fetchEventById = useCallback(
    async (eventId) => {
      try {
        const res = await axios.get(`${apiDomain}/api/v2/getevent`, {
          params: { eventId, t: Date.now() },
        });
        setSingleEvent(res?.data?.[0] ?? null);
      } catch (err) {
        setSingleEvent(null);
      }
    },
    [apiDomain]
  );

  // Transform participant data from API response format to component expected format
  const transformParticipantData = useCallback((rawData) => {
    if (!rawData) return [];
    
    // Handle API response wrapper (if API returns {participants: [...], next_url: ..., etc})
    if (typeof rawData === 'object' && !Array.isArray(rawData)) {
      // Check for participants array (matches getnonparticipants response format)
      if (rawData.participants !== undefined) {
        return transformParticipantData(rawData.participants);
      }
      // Check for data property
      if (rawData.data && (Array.isArray(rawData.data) || typeof rawData.data === 'object')) {
        return transformParticipantData(rawData.data);
      }
      // Check for result property
      if (rawData.result && Array.isArray(rawData.result)) {
        return transformParticipantData(rawData.result);
      }
    }
    
    // If data is already in the expected format (has participationDetails), return as-is
    if (Array.isArray(rawData) && rawData.length > 0 && rawData[0]?.participationDetails) {
      return rawData;
    }
    
    // Handle API response format like getnonparticipants (array of {id, igUser} objects)
    if (Array.isArray(rawData) && rawData.length > 0 && rawData[0]?.igUser) {
      return rawData.map(user => ({
        userId: user.id?.replace('instagram:', '') || user.igUser?.id,
        instagramBusinessAccountId: user.igUser?.id,
        name: user.igUser?.name || 'N/A',
        isiguser: true,
        joiningTime: null, // Will be populated if we have participation data
        instagramUser: {
          username: user.igUser?.username,
          followers_count: user.igUser?.followers_count,
          profile_picture_url: user.igUser?.profile_picture_url,
          biography: user.igUser?.biography,
        },
        participationDetails: user.participationDetails || [],
      }));
    }
    
    // If data is an object with participant IDs as keys (from Firebase), transform it
    if (typeof rawData === 'object' && !Array.isArray(rawData) && Object.keys(rawData).length > 0) {
      // Group participation records by user ID
      const participantMap = new Map();
      
      Object.values(rawData).forEach(participationDetail => {
        const userId = participationDetail.id;
        if (!userId) return;
        
        if (!participantMap.has(userId)) {
          participantMap.set(userId, {
            userId: userId,
            participationDetails: [],
            instagramUser: {},
            joiningTime: null,
            name: participationDetail.entityname,
            isiguser: false,
            instagramBusinessAccountId: userId,
          });
        }
        
        const participant = participantMap.get(userId);
        participant.participationDetails.push({
          event_id: participationDetail.event_id,
          city: { String: participationDetail.city },
          state: { String: participationDetail.state },
          pincode: { String: participationDetail.pincode },
          age_category: { String: participationDetail.agecategory },
          gender_category: { String: participationDetail.gendercategory },
          followers_count_category: { String: participationDetail.followerscountcategory },
          generic_category: { String: participationDetail.genericcategory },
          international: participationDetail.international,
          participationtime: participationDetail.participationtime,
        });
        
        // Set joining time from first participation
        if (!participant.joiningTime) {
          participant.joiningTime = participationDetail.participationtime;
        }
      });
      
      const result = Array.from(participantMap.values());
      return result;
    }
    
    // If it's already an array, return as-is
    if (Array.isArray(rawData)) {
      return rawData;
    }
    
    return [];
  }, []);

  useEffect(() => {
    let cancelled = false;
    let retryTimer = null;
    const fetchData = async (attempt = 0) => {
      // Right after admin login the auth cookie can lag the first SPA render
      // (OAuth redirect race). Previously a direct click to Participants /
      // Sponsors hit this effect with no token, gave up, and left an empty
      // "No data" table that only a manual reload fixed. Instead keep the
      // loader visible and retry for a few seconds until the cookie appears.
      const accessToken = Cookies.get("fbauthToken");
      if (!accessToken) {
        if (attempt < 12 && !cancelled) {
          setLoading(true);
          retryTimer = setTimeout(() => fetchData(attempt + 1), 500);
          return;
        }
        setError("No access token found in cookies");
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);

        // Other parallel API calls
        const [
          locationRes,
          venuesRes,
          participantsMonthlyRes,
          participantsTotalRes,
          participantRes,
          noneParticipantRes,
          sponsorsRes,
          filteredSponsorRes,
          supportRes,
          collabTicketsRes,
          winnerRes,
          notificationsRes,
          referralsRes,
          rewardsRes,
          userInfoRes,
        ] = await Promise.all([
          axios
            .get(`${apiDomain}/api/v2/getlocations`, {
              params: { access_token: accessToken },
            })
            .catch(() => null),
          axios
            .get(`${apiDomain}/api/v2/venues`, {
              params: { access_token: accessToken },
            })
            .catch(() => null),
          axios
            .get(`${apiDomain}/api/v2/monthlyparticipants`, {
              params: { access_token: accessToken },
            })
            .catch(() => null),
          axios
            .get(`${apiDomain}/api/v2/totalparticipants`, {
              params: { access_token: accessToken },
            })
            .catch(() => null),
          axios
            .get(`${apiDomain}/api/v2/exploreparticipants`, {
              params: { access_token: accessToken, pageSize: 2000 },
            })
            .catch(() => null),
          axios
            .get(`${apiDomain}/api/v2/getnonparticipants`, {
              params: { access_token: accessToken },
            })
            .catch(() => null),
          axios
            .get(`${apiDomain}/api/v2/getallsponsors`, {
              params: { access_token: accessToken },
            })
            .catch(() => null),
          axios
            .get(`${apiDomain}/api/v2/getfilteredsponsors`, {
              params: { access_token: accessToken },
            })
            .catch(() => null),
          axios
            .get(`${supportDomain}/support/tickets`, {
              params: { access_token: accessToken, isCollabTicket: false },
            })
            .catch(() => null),
          axios
            .get(`${supportDomain}/support/tickets`, {
              params: { access_token: accessToken, isCollabTicket: true },
            })
            .catch(() => null),
          axios
            .get(`${apiDomain}/api/v2/getallwinners`, {
              params: { access_token: accessToken },
            })
            .catch(() => null),
          axios
            .get(`${notificationDomain}/notifications/all`, {
              params: { limit: 100 },
            })
            .catch(() => null),
          axios
            .get(`${apiDomain}/api/v2/referral/admin/all`, {
              params: { limit: 100 },
            })
            .catch(() => null),
          axios.get(`${apiDomain}/api/v2/referral-data/get`).catch(() => null),
          axios
            .get(`${apiDomain}/api/v2/userinformation`, {
              params: { limit: 100 },
            })
            .catch(() => null),
        ]);

        if (locationRes) {
          setLocation(locationRes.data);
        }
        if (venuesRes) {
          setVenues(venuesRes.data);
        }
        if (participantRes) {
          const transformedParticipants = transformParticipantData(participantRes.data);
          setParticipant(transformedParticipants);
        }
        if (participantsMonthlyRes) {
          setParticipantsMonthly(participantsMonthlyRes.data);
        }
        if (participantsTotalRes) {
          setParticipantsTotal(participantsTotalRes.data);
        }
        if (noneParticipantRes) {
          setNoneParticipated(noneParticipantRes.data);
        }
        if (sponsorsRes) {
          setSponsors(sponsorsRes.data);
        }
        if (filteredSponsorRes) {
          setFilteredSposnor(filteredSponsorRes.data);
        }
        if (supportRes) {
          setSupport(supportRes.data);
        }
        if (collabTicketsRes) {
          setCollabTickets(collabTicketsRes.data);
        }
        if (winnerRes) {
          setWinners(winnerRes.data);
        }
        if (notificationsRes) {
          setNotifications(notificationsRes.data);
        }
        if (referralsRes) {
          setReferrals(referralsRes.data);
        }
        if (rewardsRes) {
          setRewards(rewardsRes.data);
          console.log("✅ Rewards Data: - DataContext.js:291", rewardsRes.data);
        }
        if (userInfoRes) {
          setUsersInfo(userInfoRes.data);
          console.log("✅ User Info: - DataContext.js:295", userInfoRes.data);
        }

        // Always fetch events list; default behavior: ongoing events
        await fetchAllEvents('ongoing');
        // Fetch all event names for dropdowns
        await fetchAllEventNames();
      } catch (err) {
        setError(err.message || "An error occurred while fetching data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [apiDomain, fetchAllEvents, transformParticipantData]);

  return (
    <DataContext.Provider
      value={{
        eventData,
        singleEvent,
        fetchAllEvents,
        fetchEventById,
        fetchAllEventNames,
        allEventNames,
        allEventsSummary,
        prependEvent, // 👉 EXPOSED
        upsertEvent, 
        location,
        venues,
        participant,
        participantsMonthly,
        participantsTotal,
        noneParticipated,
        sponsors,
        filteredSponsor,
        support,
        collabTickets,
        winners,
        notifications,
        referrals,
        rewards,
        usersInfo,
        loading,
        error,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

export { DataContext, DataProvider };

// import { createContext, useState, useEffect, useCallback } from "react";
// import axios from "axios";
// import Cookies from "js-cookie";
// import { appendIgUserParam } from "../../components/loginPage/loginHandelSystem/hadelFunction";

// const DataContext = createContext();

// // Helper to mask tokens for security
// const MASK = (t) => (!t ? t : t.length > 12 ? `${t.slice(0,6)}…${t.slice(-4)}` : t);

// const DataProvider = ({ children }) => {
//   const apiDomain = process.env.REACT_APP_API_DOMAIN;
//   const supportDomain = process.env.REACT_APP_SUPPORT_DOMAIN;
//   const notificationDomain = process.env.REACT_APP_NOTIFICATION_DOMAIN;

//   // State variables
//   const [eventData, setEventData] = useState([]);
//   const [singleEvent, setSingleEvent] = useState(null);
//   const [participantsMonthly, setParticipantsMonthly] = useState([]);
//   const [participantsTotal, setParticipantsTotal] = useState([]);
//   const [participant, setParticipant] = useState([]);
//   const [noneParticipated, setNoneParticipated] = useState([]);
//   const [location, setLocation] = useState([]);
//   const [venues, setVenues] = useState([]);
//   const [sponsors, setSponsors] = useState([]);
//   const [filteredSponsor, setFilteredSposnor] = useState([]);
//   const [support, setSupport] = useState([]);
//   const [collabTickets, setCollabTickets] = useState([]);
//   const [winners, setWinners] = useState([]);
//   const [notifications, setNotifications] = useState([]);
//   const [referrals, setReferrals] = useState([]);
//   const [rewards, setRewards] = useState([]);
//   const [usersInfo, setUsersInfo] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   // Logs to debug cookies
//   const _dumpCookieSnapshot = (label) => {
//     const snap = {
//       fbauthToken: MASK(Cookies.get('fbauthToken')),
//       igauthtoken: MASK(Cookies.get('igauthtoken')),
//       iguser: Cookies.get('iguser') || null,
//       loginTime: Cookies.get('loginTime') || null,
//     };
//     console.group(`[DataContext] ${label}`);
//     console.table(snap);
//     console.groupEnd();
//     return snap;
//   };

//   // Building auth params based on the cookies
//   const buildAuthParams = () => {
//     const igUser = Cookies.get('iguser') === 'true';
//     const fbToken = Cookies.get('fbauthToken') || null;
//     const igToken = Cookies.get('igauthtoken') || null;
//     const accessToken = igUser ? igToken : fbToken;
//     const base = accessToken ? { access_token: accessToken } : null;
//     const withFlag = base ? appendIgUserParam(base) : null;

//     if (withFlag) {
//       console.log('[DataContext] Auth Params:', { ...withFlag, access_token: MASK(withFlag.access_token) });
//     }

//     return withFlag;
//   };

//   // Fetching all events
//   const fetchAllEvents = useCallback(
//     async (authParams) => {
//       try {
//         const params = authParams || buildAuthParams();
//         if (!params) {
//           console.warn('[fetchAllEvents] No auth params, skipping.');
//           setEventData([]);
//           return;
//         }

//         console.log('[fetchAllEvents] Using params', { ...params, access_token: MASK(params.access_token) });

//         const listRes = await axios.get(`${apiDomain}/api/v2/getallevents`, { params });
//         console.log("✅ Events fetched:", listRes?.data?.length);

//         if (listRes?.data?.length > 0) {
//           const ids = listRes.data.map((e) => e.event_id);
//           const detailPromises = ids.map((id) =>
//             axios
//               .get(`${apiDomain}/api/v2/getevent`, { params: { ...params, eventId: id } })
//               .then((res) => res?.data?.[0] || null)
//               .catch((err) => {
//                 console.error(`❌ Error fetching event ${id}`, err);
//                 return null;
//               })
//           );
//           const details = await Promise.all(detailPromises);
//           setEventData(details.filter(Boolean));
//         } else {
//           setEventData([]);
//         }
//       } catch (err) {
//         console.error("❌ Error fetching all events:", err);
//         setEventData([]);
//       }
//     },
//     [apiDomain]
//   );

//   // Fetch single event by ID
//   const fetchEventById = useCallback(
//     async (eventId) => {
//       try {
//         const params = buildAuthParams();
//         if (!params) throw new Error("No access token found in cookies");
//         const res = await axios.get(`${apiDomain}/api/v2/getevent`, { params: { ...params, eventId } });
//         setSingleEvent(res?.data?.[0] || null);
//       } catch (err) {
//         console.error("❌ Error fetching event by ID:", err);
//         setSingleEvent(null);
//       }
//     },
//     [apiDomain]
//   );

//   // Fetching all required data (from multiple endpoints)
//   useEffect(() => {
//     const fetchData = async () => {
//       _dumpCookieSnapshot('before fetchData');

//       try {
//         setLoading(true);
//         setError(null);

//         const params = buildAuthParams();
//         if (!params) {
//           console.error("❌ No access token found in cookies");
//           throw new Error("No access token found in cookies");
//         }

//         console.group('[DataContext] Batch fetch with params');
//         console.log({ ...params, access_token: MASK(params.access_token) });

//         const reqs = [
//           axios.get(`${apiDomain}/api/v2/getlocations`, { params }).catch(() => null),
//           axios.get(`${apiDomain}/api/v2/venues`, { params }).catch(() => null),
//           axios.get(`${apiDomain}/api/v2/monthlyparticipants`, { params }).catch(() => null),
//           axios.get(`${apiDomain}/api/v2/totalparticipants`, { params }).catch(() => null),
//           axios.get(`${apiDomain}/api/v2/exploreparticipants`, { params }).catch(() => null),
//           axios.get(`${apiDomain}/api/v2/getnonparticipants`, { params }).catch(() => null),
//           axios.get(`${apiDomain}/api/v2/getallsponsors`, { params }).catch(() => null),
//           axios.get(`${apiDomain}/api/v2/getfilteredsponsors`, { params }).catch(() => null),
//           axios.get(`${supportDomain}/support/tickets`, { params: { ...params, isCollabTicket: false } }).catch(() => null),
//           axios.get(`${supportDomain}/support/tickets`, { params: { ...params, isCollabTicket: true } }).catch(() => null),
//           axios.get(`${apiDomain}/api/v2/getallwinners`, { params }).catch(() => null),
//           axios.get(`${notificationDomain}/notifications/all`, { params: { limit: 100 } }).catch(() => null),
//           axios.get(`${apiDomain}/api/v2/referral/admin/all`, { params: { ...params, limit: 100 } }).catch(() => null),
//           axios.get(`${apiDomain}/api/v2/referral-data/get`, { params }).catch(() => null),
//           axios.get(`${apiDomain}/api/v2/userinformation`, { params: { ...params, limit: 100 } }).catch(() => null),
//         ];

//         const [
//           locationRes, venuesRes, participantsMonthlyRes, participantsTotalRes,
//           participantRes, noneParticipantRes, sponsorsRes, filteredSponsorRes,
//           supportRes, collabTicketsRes, winnerRes, notificationsRes,
//           referralsRes, rewardsRes, userInfoRes
//         ] = await Promise.all(reqs);

//         if (locationRes) setLocation(locationRes.data);
//         if (venuesRes) setVenues(venuesRes.data);
//         if (participantRes) setParticipant(participantRes.data);
//         if (participantsMonthlyRes) setParticipantsMonthly(participantsMonthlyRes.data);
//         if (participantsTotalRes) setParticipantsTotal(participantsTotalRes.data);
//         if (noneParticipantRes) setNoneParticipated(noneParticipantRes.data);
//         if (sponsorsRes) setSponsors(sponsorsRes.data);
//         if (filteredSponsorRes) setFilteredSposnor(filteredSponsorRes.data);
//         if (supportRes) setSupport(supportRes.data);
//         if (collabTicketsRes) setCollabTickets(collabTicketsRes.data);
//         if (winnerRes) setWinners(winnerRes.data);
//         if (notificationsRes) setNotifications(notificationsRes.data);
//         if (referralsRes) setReferrals(referralsRes.data);
//         if (rewardsRes) setRewards(rewardsRes.data);
//         if (userInfoRes) setUsersInfo(userInfoRes.data);

//         console.groupEnd();

//         // Also fetch events with same params
//         await fetchAllEvents(params);
//       } catch (err) {
//         console.error("❌ Error fetching data:", err);
//         setError(err.message || "An error occurred while fetching data.");
//       } finally {
//         setLoading(false);
//         _dumpCookieSnapshot('after fetchData');
//       }
//     };

//     fetchData();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [apiDomain, supportDomain, notificationDomain, fetchAllEvents]);

//   return (
//     <DataContext.Provider
//       value={{
//         eventData,
//         singleEvent,
//         fetchAllEvents,
//         fetchEventById,
//         location,
//         venues,
//         participant,
//         participantsMonthly,
//         participantsTotal,
//         noneParticipated,
//         sponsors,
//         filteredSponsor,
//         support,
//         collabTickets,
//         winners,
//         notifications,
//         referrals,
//         rewards,
//         usersInfo,
//         loading,
//         error,
//       }}
//     >
//       {children}
//     </DataContext.Provider>
//   );
// };

// export { DataContext, DataProvider };
